import { useMemo, useState } from 'react';
import { vincularVendedorErp, type AvisoVinculoErp, type UserWithProfile } from '../../lib';
import { Modal, ModalCloseButton, avisar, mensajeDeError, useCerrarModal } from '../../components/ui-kit';

type Props = {
  /** Vendedor sincronizado desde el ERP. */
  vendedorErp: UserWithProfile;
  usuarios: UserWithProfile[];
  onClose: () => void;
  onGuardado: () => unknown;
};

/** Explicación del aviso de desfase entre el vendedor del ERP y los vendedores del GPS. */
export function textoAvisoVinculoErp(aviso?: AvisoVinculoErp | null): string {
  switch (aviso) {
    case 'solo_usuario': return 'Coincide el usuario con un vendedor del GPS, pero no la cédula.';
    case 'solo_cedula': return 'Coincide la cédula con un vendedor del GPS, pero no el usuario.';
    case 'cruzado': return 'El usuario coincide con un vendedor del GPS y la cédula con otro.';
    case 'varios': return 'Varios vendedores del GPS tienen el mismo usuario y cédula.';
    default: return '';
  }
}

function nombreDe(user: UserWithProfile): string {
  return (user.profile?.razon_social || user.username).trim();
}

/** Vendedor propio del GPS, activo, que puede recibir los viajes de un vendedor del ERP. */
function esVendedorGpsVinculable(user: UserWithProfile): boolean {
  const perfil = user.profile;
  return Boolean(
    user.id
      && perfil?.estado !== false
      && (perfil?.rol || '').toUpperCase() === 'VENDEDOR'
      && perfil?.origen_externo !== 'ERP',
  );
}

function Contenido({ vendedorErp, usuarios, onGuardado }: Omit<Props, 'onClose'>) {
  const cerrar = useCerrarModal();
  const perfil = vendedorErp.profile;
  const vinculadoActual = perfil?.vinculado_a ?? null;
  const [vendedorId, setVendedorId] = useState<string>(perfil?.vinculo_manual && vinculadoActual ? String(vinculadoActual) : '');
  const [reasignar, setReasignar] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const candidatos = useMemo(
    () => usuarios.filter(esVendedorGpsVinculable).sort((a, b) => nombreDe(a).localeCompare(nombreDe(b))),
    [usuarios],
  );
  const actual = vinculadoActual ? usuarios.find(user => user.id === vinculadoActual) : undefined;

  async function guardar() {
    if (!vendedorErp.id) return;
    setGuardando(true);
    setError('');
    try {
      const respuesta = await vincularVendedorErp(vendedorErp.id, {
        vendedor_id: vendedorId ? Number(vendedorId) : null,
        reasignar_viajes: Boolean(vendedorId) && reasignar,
      });
      const datos = respuesta.data;
      const reasignados = datos?.viajes_reasignados ?? 0;
      avisar(
        datos?.vinculado_a_username
          ? `Los viajes del ERP de ${nombreDe(vendedorErp)} se asignarán a ${datos.vinculado_a_username}.${reasignados ? ` Se reasignaron ${reasignados} viaje${reasignados === 1 ? '' : 's'}.` : ''}`
          : `${nombreDe(vendedorErp)} vuelve al vínculo automático: ningún vendedor del GPS tiene su mismo usuario y cédula.${datos?.vinculo_aviso ? ` ${textoAvisoVinculoErp(datos.vinculo_aviso)}` : ''}`,
      );
      await onGuardado();
      cerrar();
    } catch (err: unknown) {
      setError(mensajeDeError(err, 'No se pudo guardar el vínculo.'));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <>
      <div className="flex items-start justify-between gap-4 border-b border-stone-200 px-6 py-4">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-stone-950">Vincular con un vendedor del GPS</h3>
          <p className="text-sm text-stone-500">
            {nombreDe(vendedorErp)} · <span className="font-mono text-xs">{vendedorErp.username}</span>
            {perfil?.identificacion ? ` · cédula ${perfil.identificacion}` : ''}
          </p>
        </div>
        <ModalCloseButton className="ui-btn ui-btn-secondary ui-btn-sm">Cerrar</ModalCloseButton>
      </div>

      <div className="space-y-5 px-6 py-5 text-sm text-stone-700">
        <div className="rounded-[var(--radius-card)] border border-indigo-200 bg-indigo-50/70 p-4 text-indigo-900">
          <p className="font-semibold">
            {actual
              ? `Hoy sus viajes van a ${nombreDe(actual)} (${actual.username}) · ${perfil?.vinculo_manual ? 'vínculo manual' : 'automático: mismo usuario y cédula'}.`
              : 'Hoy no está vinculado: sus viajes quedan a nombre del vendedor del ERP.'}
          </p>
          <p className="mt-1 text-xs text-indigo-800">
            Los viajes que envíe el ERP para este vendedor se asignan al vendedor del GPS vinculado, que los ve en su panel.
            El vínculo manual se mantiene aunque el ERP vuelva a sincronizar al vendedor.
          </p>
        </div>

        {!actual && perfil?.vinculo_aviso && (
          <div className="rounded-[var(--radius-card)] border border-amber-200 bg-amber-50 p-4 text-amber-900" role="status">
            <p className="font-semibold"><i className="fa-solid fa-triangle-exclamation mr-1.5" aria-hidden="true" />Revisar datos</p>
            <p className="mt-1 text-xs">
              {textoAvisoVinculoErp(perfil.vinculo_aviso)} Para que se vincule solo, el usuario y la cédula deben ser iguales en el ERP y en el GPS. También puedes vincularlo a mano aquí.
            </p>
          </div>
        )}

        <label className="block space-y-2">
          <span className="font-medium text-stone-800">Vendedor del GPS que recibe sus viajes</span>
          <select value={vendedorId} onChange={event => setVendedorId(event.target.value)} className="ui-input w-full" disabled={guardando}>
            <option value="">Automático (mismo usuario y cédula)</option>
            {candidatos.map(user => (
              <option key={user.id} value={String(user.id)}>
                {nombreDe(user)} · {user.username}{user.profile?.identificacion ? ` · ${user.profile.identificacion}` : ''}
              </option>
            ))}
          </select>
          {candidatos.length === 0 && (
            <span className="block text-xs text-amber-700">No hay vendedores propios del GPS activos para vincular.</span>
          )}
        </label>

        {vendedorId && (
          <label className="flex items-start gap-3 rounded-[var(--radius-card)] border border-stone-200 bg-stone-50 p-3">
            <input
              type="checkbox"
              checked={reasignar}
              onChange={event => setReasignar(event.target.checked)}
              disabled={guardando}
              className="mt-0.5 h-4 w-4 accent-[var(--freisa-red)]"
            />
            <span>
              <span className="font-medium text-stone-800">Pasar también los viajes ya creados</span>
              <span className="block text-xs text-stone-500">Los viajes del ERP que hoy están a nombre de {nombreDe(vendedorErp)} pasan al vendedor elegido.</span>
            </span>
          </label>
        )}

        {error && <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{error}</p>}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <ModalCloseButton className="ui-btn ui-btn-secondary" disabled={guardando}>Cancelar</ModalCloseButton>
          <button type="button" onClick={() => void guardar()} disabled={guardando} className="ui-btn ui-btn-primary">
            {guardando ? 'Guardando...' : vendedorId ? 'Guardar vínculo' : 'Usar vínculo automático'}
          </button>
        </div>
      </div>
    </>
  );
}

/** Vínculo manual de un vendedor del ERP con un vendedor propio del GPS (cuando no coinciden usuario y cédula). */
export default function VincularVendedorErpModal({ onClose, ...props }: Props) {
  return (
    <Modal onClose={onClose} maxWidthClassName="max-w-lg" showCloseButton={false} ariaLabel="Vincular vendedor del ERP">
      <Contenido {...props} />
    </Modal>
  );
}
